import { priceLabel } from "@sill/shared/lib/price-label";
/**
 * Wire-DTO → view-model adapters (the prod path).
 *
 * The server's roster carries the scope-projected `profile`, link metadata,
 * and backend-owned verification status. We derive the remaining display shape
 * here:
 *
 *   - verification tier: direct from the backend-owned `verification_status`.
 *   - financials: built from the projected magnitudes when present; ABSENT
 *     magnitudes stay absent (the field shows "Not shared at this tier"), so the
 *     status_only contract survives end-to-end.
 *   - underwriting: only when a credit range is present (full_financial).
 *
 * The fixtures bypass this and supply richer hand-authored view models; this
 * adapter is what runs against the live API.
 */

import type {
  ActivityFeedItem,
  AffordabilityMovementVM,
  AffordabilityVM,
  BillingStatusVM,
  ClientActivityEvent,
  ClientConnection,
  ClientNoteVM,
  ClientVM,
  ContactFunnelSummaryVM,
  ContactVM,
  FinancialField,
  PersonVM,
  FoundingVM,
  ImportPreviewVM,
  ImportResultVM,
  InviteResultVM,
  NotificationPrefVM,
  ProProfileVM,
  RateCardRowVM,
  TodaySignalVM,
  Underwriting,
} from "./view-model";
import type {
  AffordabilityMovementResponse,
  BillingStatus,
  ContactFunnelSummary,
  FunnelState,
  ImportCommitResult,
  ImportPreview,
  InviteSendResult,
  CRMTask,
  NotificationPref,
  ProActivityEvent,
  ProClientDetail,
  ProClientNote,
  ProClientRow,
  ProRateCardRow,
  ProSentMessage,
  FinancialVerificationTier,
  ProContactDTO,
  ProfileResponse,
  ProProfile,
  SharingLevel,
  TodayDraftResponse,
  TodayGroup,
  TodayMarketplaceRequestsSummary,
  TodayQueueItem,
  TodayQueueResponse,
  ProRole,
  ProPersonRow,
} from "./types";
import { isEnrichmentState } from "./types";
import type { TierRank } from "../components/tier/tier";
import { tierMeta } from "../components/tier/tier";
import {
  formatBuyerType,
  formatCreditRange,
  formatPriceBand,
  formatTimeline,
  formatTimelineShort,
} from "./format";
import { messagedDisplayLabel } from "./messaged-display";
import { timelineBucket } from "./audience-filters";
import { isCardExpired } from "./billing";
import { marketplaceCatalogEntries } from "./notification-channel-matrix";
import type {
  TodayDraftVM,
  TodayGroupVM,
  TodayMarketplaceRequestsVM,
  TodayQueueItemVM,
  TodayQueueVM,
  TodayTaskItemVM,
} from "./view-model";

/** Initials from a display name (max 2), e.g. "Nina Park" → "NP". */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

function tierRank(status: FinancialVerificationTier | undefined): TierRank {
  switch (status) {
    case "connected":
      return 3;
    case "document":
    case "pre_approval":
      return 2;
    default:
      return 1;
  }
}

function statusStamp(
  status: FinancialVerificationTier | undefined,
): Pick<ClientVM["financials"]["income"], "verb" | "kind"> {
  switch (tierRank(status)) {
    case 3:
      return { verb: "Verified", kind: "bank" };
    case 2:
      return { verb: "Verified", kind: "doc" };
    default:
      return { verb: "Self-reported", kind: "self" };
  }
}

/** USD with thousands separators, no cents. */
function usd(n: number): string {
  return `$${Math.round(n).toLocaleString("en-US")}`;
}

/** Render the server-projected preference or target bracket. */
function budgetDisplay(p: ProfileResponse | null): string {
  if (!p) return "Not shared";
  // SILL-359: the projected band is a raw `<lo>k-<hi>k` token — humanize it.
  return formatPriceBand(p.target_price_band);
}

/** Coarse credit band (agent-safe). Full FICO never reaches the agent view. */
function creditBandDisplay(p: ProfileResponse | null): string {
  // SILL-359: `credit_score_range` is a storage enum (e.g. "excellent_740+").
  return formatCreditRange(p?.credit_score_range);
}

/** Underwriting block — only derivable at full_financial with a credit range. */
function deriveUnderwriting(p: ProfileResponse | null): Underwriting | null {
  if (!p || !p.credit_score_range) return null;
  const creditRange = formatCreditRange(p.credit_score_range);
  const reserves =
    p.reserve_floor_months != null ? `${p.reserve_floor_months} months` : "—";
  const dti =
    p.comfort_dti_pct != null ? `${Math.round(p.comfort_dti_pct)}%` : "—";
  return {
    // The projection carries a credit RANGE, not a point score; surface the
    // range midpoint label rather than fabricating a FICO.
    creditScore: 0,
    creditPull: `Range ${creditRange}`,
    dti,
    frontDti: "—",
    reserves,
    scenarios: [],
  };
}

/**
 * Affordability headline (SILL-384 M6). Projects the readiness/qualification
 * OUTCOME the DTO already carries — never computes it. Nil money fields become
 * a PENDING state (null) rather than a fabricated $0, and the down-payment
 * factor maps nil → "pending" (scenarios 29/30). Movement + down-payment-ready
 * are partner-lens only on the wire, so they simply stay null on a projection
 * that didn't carry them.
 */
function deriveAffordability(p: ProfileResponse | null): AffordabilityVM {
  const qualPrice = p?.sill_qual_price != null ? usd(p.sill_qual_price) : null;
  // A band needs BOTH ends to be real, positive dollars. Post SILL-30 the
  // derive-on-read path omits the low end entirely; the snapshot path can emit
  // a literal $0 low when one evaluated product does not qualify. Treat either
  // as "no band" so a null/`$0` low never forms a `$0–$X` range — the figure
  // falls back to the single qual ceiling ("Up to $X") downstream.
  const lo = p?.affordability_band_low;
  const hi = p?.affordability_band_high;
  const hasBand = lo != null && lo > 0 && hi != null && hi > 0;
  const band = hasBand ? `$${kDollars(lo)}–$${kDollars(hi)}` : null;
  // Tight band for the roster column (one "$", one "k"): "$495–575k".
  const bandCompact = hasBand
    ? `$${Math.round(lo / 1000)}–${Math.round(hi / 1000)}k`
    : null;
  const targetBudget =
    p?.target_price != null
      ? usd(p.target_price)
      : p?.target_price_band
        ? formatPriceBand(p.target_price_band)
        : null;
  const downPayment =
    p?.down_payment_ready == null
      ? "pending"
      : p.down_payment_ready
        ? "ready"
        : "not_ready";
  return {
    qualPrice,
    band,
    bandCompact,
    movement: deriveBandMovement(p?.affordability_movement),
    downPayment,
    targetBudget,
    targetBudgetLabel: p?.target_price != null ? "Target price" : priceLabel(p?.price_kind, p?.price_approximate),
    pending: qualPrice === null,
    // Readiness OUTCOME the agent communicates over (2026-06-10). DISPLAYED off
    // the projection, never computed. A null score (DNQ) is preserved so the
    // card renders the segment label, never a fabricated 0.
    score: p?.readiness_score ?? null,
    segment: p?.readiness_segment ?? null,
    segmentLabel: p?.readiness_label ?? null,
    // Exact target price (a fixed number), compact. Never a band — the score is
    // computed relative to this single target.
    target: p?.target_price != null ? `$${kDollars(p.target_price)}` : null,
  };
}

/** Recent band-high movement → display VM. Null unless both endpoints differ. */
function deriveBandMovement(
  m: AffordabilityMovementResponse | undefined,
): AffordabilityMovementVM | null {
  if (!m || m.band_high_from == null || m.band_high_to == null) return null;
  const delta = m.band_high_to - m.band_high_from;
  if (delta === 0) return null;
  return {
    direction: delta > 0 ? "up" : "down",
    amount: `$${kDollars(Math.abs(delta))}`,
    asOf: m.as_of ? relativeTime(m.as_of) : "recently",
  };
}

/** Round dollars to a coarse "k" magnitude, e.g. 682000 → "682k". */
function kDollars(n: number): string {
  return `${Math.round(n / 1000)}k`;
}

/**
 * Maps the single backend-composed People response. This deliberately does not
 * combine the legacy Clients and Contacts endpoints in the browser.
 */
export function adaptPersonRow(row: ProPersonRow): PersonVM {
  const full = `${row.first_name} ${row.last_name}`.trim();
  const localPart = row.email?.split("@")[0] ?? "";
  const name = full || localPart || "Unnamed person";
  // Enrichment.data is unreachable outside the "available" arm (design §3.2)
  // — the discriminated union makes this narrowing exhaustive at compile time.
  const source = row.enrichment.state === "available" ? row.enrichment.data : undefined;
  const bandLow = source?.affordability_low;
  const bandHigh = source?.affordability_high;
  // Post-SILL-30 the wire carries only band_high (the fabricated low was
  // deleted), so a single-sided high renders as "Up to $X" instead of a range.
  const band =
    bandLow != null && bandLow > 0 && bandHigh != null && bandHigh > 0
      ? `$${kDollars(bandLow)}–$${kDollars(bandHigh)}`
      : bandHigh != null && bandHigh > 0
        ? `Up to $${kDollars(bandHigh)}`
        : null;
  return {
    contactId: row.contact_id,
    name,
    avatar: initials(name),
    // Unknown stays unknown: a connected person whose enrichment is locked or
    // unavailable has a HIDDEN tier, not tier 1 — collapsing to "Self-reported"
    // is a false claim about a possibly bank-verified buyer. A not-connected
    // contact's data genuinely is self-reported (the agent's own book entry).
    verificationTier: source
      ? tierRank(source.verification_status)
      : row.sill_status === "connected"
        ? undefined
        : 1,
    email: row.email || undefined,
    phone: row.phone || undefined,
    area: row.area || undefined,
    tags: row.tags ?? [],
    relationshipStage: row.relationship_stage,
    stageUpdatedAt: row.stage_updated_at,
    sillStatus: row.sill_status,
    timeline: formatTimeline(row.timeline),
    timelineShort: formatTimelineShort(row.timeline),
    engagement: {
      attention: row.engagement.attention,
      actionableReason: row.engagement.actionable_reason,
      nextTaskDueAt: row.engagement.next_task_due_at,
      latestNoteAt: row.engagement.latest_note_at,
      latestInviteAt: row.engagement.latest_invite_at,
      latestActivityAt: row.engagement.latest_activity_at,
      lastOutboundAt: row.engagement.last_outbound_at,
      lastOutboundStatus: row.engagement.last_outbound_status,
    },
    affordability: source && band
      ? {
          band,
          verificationLabel: source.verification_status
            ? tierMeta(tierRank(source.verification_status)).label
            : undefined,
          movement: deriveBandMovement(source.movement),
        }
      : undefined,
    enrichmentState: row.enrichment.state,
    capabilities: {
      canInvite: row.capabilities.can_invite.allowed,
      canEmail: row.capabilities.can_email.allowed,
      canSchedule: row.capabilities.can_schedule.allowed,
      canTask: row.capabilities.can_task.allowed,
      canNote: row.capabilities.can_note.allowed,
      canInAppMessage: row.capabilities.can_in_app_message.allowed,
      canRestore: row.capabilities.can_restore.allowed,
      canDelete: row.capabilities.can_delete.allowed,
    },
  };
}

const UNSHARED_FIELD: FinancialField = {
  verb: "Not shared at this tier",
  date: null,
  kind: "self",
};

/**
 * Best-effort People→legacy-ClientVM bridge (plan 377 M5 — the roster
 * GET /clients endpoint useClients() used to read from is retired; People
 * (GET /people) is the one roster source now, design spec §4.1). Every
 * CONNECTED row adapts; anyone else is null (a contact, not a client).
 *
 * The campaign composer's presets read the recency and follow-up fields, so
 * they are derived from the People engagement signal (the pro's own last
 * touch, else when the contact was added; the retained Today cue), exactly as
 * the backend's crm.MatchesSegment reads them. They used to be hardcoded,
 * which left "All active", "Needs follow-up", and "Going cold" matching no
 * client at all.
 *
 * A connected client whose financials are locked or unavailable still adapts:
 * it can be emailed, it just shows no tier or band. Its profile id is not on
 * the wire, so it is keyed by contact id (`pickByContactId`) and a campaign
 * names it by contact, which the backend resolves to the client.
 *
 * DEGRADED relative to the old profile-keyed roster/detail read: raw
 * financials, underwriting, and per-message activity are NOT part of the
 * People/Enrichment contract (Enrichment carries ONLY the readiness
 * OUTCOME — band/movement/verification — never raw dollars, crm/enrichment.go)
 * so those fields render their "not shared"/empty states here. Callers that
 * need the rich detail (PersonDetailPage's connected arm) use
 * useClientDetail(contactId) → GET /people/:contactId/client-detail instead,
 * which is NOT degraded.
 */
export function personRowToClientVM(row: ProPersonRow): ClientVM | null {
  if (row.sill_status !== "connected") return null;
  const person = adaptPersonRow(row);
  const profileId =
    row.enrichment.state === "available" ? row.enrichment.data.profile_id : undefined;
  const connection: ClientConnection = {
    linkId: "",
    role: "agent",
    sharingLevel: "status_only",
    funnelState: "linked",
  };
  const minutesAgo = minutesSince(person.engagement.latestActivityAt ?? row.created_at);
  return {
    id: profileId ?? row.contact_id,
    ...(profileId ? {} : { pickByContactId: true }),
    enrichmentState: row.enrichment.state,
    name: person.name,
    email: person.email,
    phone: person.phone,
    avatar: person.avatar,
    tier: person.verificationTier ?? 1,
    timeline: person.timeline,
    timelineShort: timelineBucket(row.timeline) || person.timelineShort,
    lastActivity: person.engagement.latestActivityAt
      ? relativeTime(person.engagement.latestActivityAt)
      : "—",
    lastSentAt: person.engagement.lastOutboundAt,
    lastSentLabel: person.engagement.lastOutboundAt
      ? `Last sent ${relativeTime(person.engagement.lastOutboundAt)}`
      : undefined,
    minutesAgo,
    changed: "",
    newEvents: 0,
    activeThisWeek: minutesAgo <= 7 * 24 * 60,
    changedThisWeek: minutesAgo <= 7 * 24 * 60,
    needsResponse: Boolean(person.engagement.actionableReason),
    area: person.area ?? "",
    goal: "",
    budget: person.affordability?.band ?? "—",
    budgetLabel: "Buying power",
    creditBand: person.affordability?.verificationLabel ?? "Not shared",
    financials: { income: UNSHARED_FIELD, savings: UNSHARED_FIELD, debts: UNSHARED_FIELD },
    underwriting: null,
    activity: [],
    connection,
    contactId: person.contactId,
    contactTags: person.tags,
    stage: person.relationshipStage as ClientVM["stage"],
    stageUpdatedAt: person.stageUpdatedAt,
  };
}

/**
 * Best-effort People→legacy-ContactVM bridge (plan 377 M5 — the contacts
 * roster GET /contacts endpoint useContacts() used to read from is retired;
 * People (GET /people) is the one roster source now, design spec §4.1).
 * Every row (connected or not) adapts — unlike personRowToClientVM, which
 * only accepts connected rows.
 */
export function personRowToContactVM(row: ProPersonRow, contactNote = ""): ContactVM {
  const person = adaptPersonRow(row);
  const funnelState: FunnelState =
    row.sill_status === "connected"
      ? "linked"
      : row.sill_status === "joined_sill"
        ? "signed_up"
        : row.sill_status === "invited"
          ? "invited"
          : "added";
  return {
    id: row.contact_id,
    name: person.name,
    avatar: person.avatar,
    email: row.email,
    phone: row.phone,
    firstName: row.first_name,
    lastName: row.last_name,
    notes: contactNote,
    tags: row.tags ?? [],
    source: row.source,
    funnelState,
    linkedProfileId:
      row.enrichment.state === "available" ? row.enrichment.data.profile_id : undefined,
    added: "",
    area: row.area ?? "",
    horizon: row.timeline ?? "",
    archivedAt: row.archived_at ?? null,
    stage: row.relationship_stage,
    stageUpdatedAt: row.stage_updated_at,
  };
}

function monthlyDebtsTotal(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;

  let total = 0;
  let found = false;
  for (const value of Object.values(v)) {
    if (typeof value === "number" && Number.isFinite(value)) {
      total += value;
      found = true;
    }
  }
  return found ? total : null;
}

/** Build the financials trio, honoring field-presence-is-the-contract. */
function deriveFinancials(
  sharing: SharingLevel,
  p: ProfileResponse | null,
  status: FinancialVerificationTier | undefined,
): ClientVM["financials"] {
  const shared = sharing === "full_financial" && !!p;
  const unshared = {
    verb: "Not shared at this tier",
    date: null,
    kind: "self" as const,
  };
  const stamp = statusStamp(status);

  const income =
    shared && p?.household_income != null
      ? { value: `${usd(p.household_income)} / yr`, ...stamp, date: null }
      : shared
        ? { ...stamp, date: null }
        : unshared;

  const savings =
    shared && p?.savings != null
      ? { value: usd(p.savings), ...stamp, date: null }
      : shared
        ? { ...stamp, date: null }
        : unshared;

  const debtsVal = monthlyDebtsTotal(p?.monthly_debts);
  const debts =
    shared && debtsVal != null
      ? { value: `${usd(debtsVal)} / mo`, ...stamp, date: null }
      : shared
        ? { ...stamp, date: null }
        : unshared;

  return { income, savings, debts };
}

/**
 * WS7 (messaging-inbox IA): a `thread_message` activity event renders as a
 * readable in-app-message label derived from its `sender_role` — the only
 * payload key the projector allowlists (body-free by construction, D5).
 * Deliberately DISTINCT from the `messaged` label ("Messaged · email"): the
 * feed must read which channel carried the touch. Phrased from the CLIENT's
 * perspective like every other feed row ("Sent a message", not "You were
 * messaged") — the pro side of the pair may be a different pro at
 * status_only scope, so the label never claims the viewer sent it.
 */
export function threadMessageLabel(senderRole: string | undefined): string {
  switch (senderRole) {
    case "consumer":
      return "Sent a message · in-app";
    case "pro":
      return "Received a message · in-app";
    case "system":
      return "Conversation update";
    default:
      return "New message · in-app";
  }
}

/** Map one wire activity event to the view-model activity item. */
export function adaptActivityEvent(e: ProActivityEvent): ClientActivityEvent {
  const p = e.payload;
  // SILL-372: a profile_updated row should name the meaningful fields that
  // moved (e.g. "Updated reserve floor and buying power") rather than the
  // generic "Updated their profile". An explicit server headline still wins;
  // the changed_fields summary slots in just above the generic per-type label.
  // SILL-384 t16: a messaged event renders as the channel-derived display label
  // ("messaged · email") — never a server headline/body (the body is on the
  // projector denylist and never reaches us). Resolve it before the generic
  // chain so a stray payload key can't shadow the channel mapping.
  const text =
    e.event_type === "messaged"
      ? messagedDisplayLabel(stringPayload(p.channel))
      : e.event_type === "messaged_pro"
        ? "Buyer messaged you"
        : e.event_type === "thread_message"
          ? threadMessageLabel(stringPayload(p.sender_role))
          : e.event_type === "link_changed"
          ? // `action` is a raw state token ("linked"/"unlinked"), not a display
            // string — capitalize it for the feed instead of rendering lowercase.
            (sentenceCase(stringPayload(p.action)) || describeEvent(e.event_type))
          : (p.headline ??
            p.message ??
            p.action ??
            summarizeChangedFields(p.changed_fields) ??
            describeEvent(e.event_type));
  return {
    source: "activity",
    type: e.event_type,
    text,
    address: typeof p.address === "string" ? p.address : undefined,
    price: typeof p.list_price === "number" ? usd(p.list_price) : undefined,
    time: relativeTime(e.occurred_at),
  };
}

export function adaptSentMessage(m: ProSentMessage): ClientActivityEvent {
  const outcome = m.outcome ?? "sent";
  const text =
    outcome === "sent"
      ? m.subject?.trim() || "Sent message"
      : outcome === "skipped"
        ? "Message not sent"
        : outcome === "failed"
          ? "Message failed"
          : "Message needs attention";
  return {
    source: "sent_message",
    type: "messaged",
    text,
    // No-send, failed, and attention rows are metadata only. Keep this guard
    // in the adapter as defense in depth for a malformed or future API reply.
    subject: outcome === "sent" ? m.subject : undefined,
    body: outcome === "sent" ? m.body : undefined,
    channel: m.channel,
    reason: outcome === "sent" ? undefined : safeOutcomeReason(m.reason),
    time: relativeTime(m.sent_at),
  };
}

function safeOutcomeReason(reason: string | undefined): string | undefined {
  switch (reason) {
    case "not_connected":
    case "stopped":
    case "not_opted_in":
    case "suppressed":
    case "muted":
    case "rate_limited":
    case "cancelled":
    case "client_only_filter":
    case "eligibility_denied":
    case "not_submitted":
    case "provider_delivery_failed":
    case "provider_outcome_unknown":
      return reason;
    default:
      return reason ? "outcome_details_unavailable" : undefined;
  }
}

function mergeTimelineEntries(
  activity: ProActivityEvent[],
  sentMessages: ProSentMessage[],
): ClientActivityEvent[] {
  const activityEntries = activity.map((event) => ({
    at: parseTimelineTimestamp(event.occurred_at),
    event,
    adapted: adaptActivityEvent(event),
  }));
  const matchedActivity = new Set<number>();
  sentMessages.forEach((message) => {
    const matchedIndex = findMatchingMessagedActivity(
      activityEntries,
      matchedActivity,
      message,
    );
    if (matchedIndex !== null) matchedActivity.add(matchedIndex);
  });
  const entries = [
    ...activityEntries
      .filter((_, index) => !matchedActivity.has(index))
      .map((entry) => ({ at: entry.at, event: entry.adapted, source: 0 })),
    ...sentMessages.map((event) => ({
      at: parseTimelineTimestamp(event.sent_at),
      event: adaptSentMessage(event),
      source: 1,
    })),
  ];
  return entries
    .sort((a, b) => {
      const atA = a.at ?? Number.NEGATIVE_INFINITY;
      const atB = b.at ?? Number.NEGATIVE_INFINITY;
      if (atB !== atA) return atB - atA;
      return a.source - b.source;
    })
    .map(({ event }) => event);
}

const SENT_MESSAGE_MATCH_WINDOW_MS = 5_000;

function findMatchingMessagedActivity(
  activityEntries: Array<{
    at: number | null;
    event: ProActivityEvent;
  }>,
  matchedActivity: Set<number>,
  sentMessage: ProSentMessage,
): number | null {
  if (sentMessage.outcome && sentMessage.outcome !== "sent") return null;
  const sentAt = parseTimelineTimestamp(sentMessage.sent_at);
  const channel = stringPayload(sentMessage.channel);
  if (sentAt === null || !channel) return null;

  let bestIndex: number | null = null;
  let bestDelta = Number.POSITIVE_INFINITY;

  for (const [index, entry] of activityEntries.entries()) {
    if (matchedActivity.has(index)) continue;
    if (entry.event.event_type !== "messaged") continue;
    if (stringPayload(entry.event.payload.channel) !== channel) continue;
    if (entry.at === null) continue;

    const delta = Math.abs(entry.at - sentAt);
    if (delta > SENT_MESSAGE_MATCH_WINDOW_MS) continue;

    if (bestIndex === null || delta < bestDelta) {
      bestIndex = index;
      bestDelta = delta;
      continue;
    }

    if (
      delta === bestDelta &&
      bestIndex !== null &&
      (entry.at ?? Number.NEGATIVE_INFINITY) >
        (activityEntries[bestIndex]?.at ?? Number.NEGATIVE_INFINITY)
    ) {
      bestIndex = index;
    }
  }

  return bestIndex;
}

function parseTimelineTimestamp(value: string): number | null {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export function adaptClientNote(n: ProClientNote): ClientNoteVM {
  return {
    id: n.id,
    body: n.body,
    author: "You",
    createdAt: relativeTime(n.created_at),
    edited: new Date(n.updated_at).getTime() > new Date(n.created_at).getTime(),
  };
}

/**
 * Human, agent-facing labels for the magnitude-free `changed_fields` keys a
 * profile_updated event carries at status_only (readiness/diff.go field names).
 * Unknown keys are humanized (underscores → spaces) so a raw key never leaks.
 */
const CHANGED_FIELD_LABELS: Record<string, string> = {
  score: "readiness score",
  segment: "readiness band",
  buying_power: "buying power",
  monthly_payment: "monthly payment",
  reserve_floor_months: "reserve floor",
  comfort_dti_pct: "comfort target",
  target_price: "target price",
  budget_monthly: "monthly budget",
  target_area: "target area",
  timeline: "timeline",
};

function changedFieldLabel(key: string): string {
  return CHANGED_FIELD_LABELS[key] ?? key.replace(/_/g, " ");
}

/**
 * Build "Updated <field>, <field>, and <field>" from the allowlisted
 * changed_fields. Returns undefined when there is nothing to summarize, so the
 * caller falls through to the generic per-type label. Caps at three named
 * fields + "and N more" so a large profile edit stays readable in a row.
 */
function summarizeChangedFields(fields?: string[]): string | undefined {
  if (!fields || fields.length === 0) return undefined;
  const labels = fields.map(changedFieldLabel).filter(Boolean);
  if (labels.length === 0) return undefined;
  return `Updated ${humanList(labels)}`;
}

function humanList(items: string[]): string {
  if (items.length === 1) return items[0]!;
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  if (items.length === 3) return `${items[0]}, ${items[1]}, and ${items[2]}`;
  const extra = items.length - 3;
  return `${items[0]}, ${items[1]}, ${items[2]}, and ${extra} more`;
}

/**
 * RECENT_ACTIVITY_WINDOW_DAYS bounds the roster "Activity" pill's recency
 * count. It MUST mirror the backend `recentActivityWindow` (7 days) in
 * backend/internal/api/professional_clients.go so the detail-path fallback
 * count reconciles with the roster DTO's server-computed recent_event_count.
 */
export const RECENT_ACTIVITY_WINDOW_DAYS = 7;

/** Count activity events whose occurred_at falls within the recent window. */
function countWithinWindow(events: ProActivityEvent[], days: number): number {
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return events.filter((e) => {
    const t = Date.parse(e.occurred_at);
    return !Number.isNaN(t) && t >= cutoff;
  }).length;
}

function describeEvent(type: ProActivityEvent["event_type"]): string {
  switch (type) {
    case "listing_analysis_run":
      return "Ran a listing analysis";
    case "verification_tier_changed":
      return "Verification changed";
    case "profile_updated":
      return "Updated their profile";
    case "rate_watch_shift":
      return "Rates shifted";
    case "going_cold":
      return "Went quiet";
    case "lease_window_hit":
      return "Entered their lease window";
    case "band_up":
      return "Moved up an affordability band";
    case "invite_accepted":
      return "Accepted your invite";
    case "link_changed":
      return "Connection changed";
    case "document_added":
      return "Added a document";
    case "bank_connected":
      return "Connected a bank";
    case "messaged":
      // Fallback only; adaptActivityEvent renders the channel-derived
      // "messaged · email" label directly (the channel isn't available here).
      return "Messaged";
    case "messaged_pro":
      // Consumer-initiated message to their pro team; no channel derivation needed.
      return "Buyer messaged you";
    case "thread_message":
      // Fallback only; adaptActivityEvent renders the sender_role-derived
      // threadMessageLabel directly (the role isn't available here).
      return "New message · in-app";
    default:
      return "Activity";
  }
}

/** RFC3339 → a coarse relative string. */
export function relativeTime(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? "Yesterday" : `${days} days ago`;
}

function minutesSince(iso?: string): number {
  if (!iso) return Number.MAX_SAFE_INTEGER;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return Number.MAX_SAFE_INTEGER;
  return Math.max(0, Math.round((Date.now() - then) / 60000));
}

/** Map a roster row (or detail) to the client view model. */
export function adaptClientRow(
  row: ProClientRow,
  activity: ProActivityEvent[] = [],
): ClientVM {
  const p = row.profile;
  const sharing = row.sharing_level;
  const tier = tierRank(row.verification_status);
  const name = displayName(p);
  // SILL-372 AC4: "Last active" reflects real activity (server-computed
  // last_activity_at) and only falls back to publish / connection dates when the
  // client has no visible recent activity — so a fresh edit reads "2 min ago"
  // instead of the stale connection date.
  const lastIso =
    row.last_activity_at ?? p?.published_at ?? row.connection.since;
  const minutesAgo = minutesSince(lastIso);
  const lastSentAt = row.last_sent_at;

  return {
    id: p?.id ?? row.connection.profile_id,
    avatar: initials(name),
    name,
    tier,
    timeline: formatTimeline(p?.timeline),
    timelineShort: formatTimelineShort(p?.timeline),
    lastActivity: lastIso ? relativeTime(lastIso) : "—",
    lastSentAt,
    lastSentLabel: lastSentAt
      ? `Last sent ${relativeTime(lastSentAt)}`
      : undefined,
    minutesAgo,
    changed: "",
    // SILL-348: the roster row carries a scope-aware recent-event count from the
    // list DTO (the pill's source of truth). The detail path has no count field
    // but passes the full activity timeline; SILL-372 AC3: count only events
    // within the same recent window the backend uses, so the detail pill
    // reconciles with the roster count instead of showing the all-time length.
    newEvents:
      row.recent_event_count ??
      countWithinWindow(activity, RECENT_ACTIVITY_WINDOW_DAYS),
    activeThisWeek: minutesAgo <= 7 * 24 * 60,
    changedThisWeek: minutesAgo <= 7 * 24 * 60,
    needsResponse: row.needs_response ?? false,
    area: p?.target_area ?? "Area not shared",
    goal: formatBuyerType(p?.buyer_type),
    budget: budgetDisplay(p),
    budgetLabel: priceLabel(p?.price_kind, p?.price_approximate),
    creditBand: creditBandDisplay(p),
    financials: deriveFinancials(sharing, p, row.verification_status),
    underwriting: deriveUnderwriting(p),
    affordability: deriveAffordability(p),
    activity: activity.map(adaptActivityEvent),
    connection: {
      linkId: row.connection.link_id,
      role: row.connection.role,
      sharingLevel: sharing,
      method: row.connection.connection_method,
      funnelState: row.connection.funnel_state,
      since: row.connection.since
        ? `Connected ${relativeTime(row.connection.since)}`
        : undefined,
    },
    contactId: row.contact_id,
    contactTags: row.contact_tags ?? [],
    // SILL-427: contact email + phone read-through from the linked crm_contacts
    // row. Normalize "" → undefined so the UI never renders a blank coordinate.
    email: row.contact_email || undefined,
    phone: row.contact_phone || undefined,
    // M8-B (plan 291): relationship-pipeline stage read-through. Absent when
    // no contact is linked yet — never a fabricated default.
    stage: row.stage,
    stageUpdatedAt: row.stage_updated_at,
    dormant: row.dormant,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// s9-client-provenance-source-chip: marketplace-sourced connection display.
// Derived purely from the existing ProConnection `connection_method` (+
// `since`) fields already flattened onto ClientConnection — no new backend
// fields consumed.
// ─────────────────────────────────────────────────────────────────────────

export function isMarketplaceConnection(
  connection: Pick<ClientConnection, "method">,
): boolean {
  return connection.method === "marketplace";
}

/**
 * Human provenance line for a marketplace-sourced client, e.g.
 * "Connected via Sill Marketplace · Apr 2026". Returns null for any other
 * connection method so callers can render conditionally. `since` already
 * carries a "Connected " prefix (see adaptClientRow below) — strip it so the
 * combined line doesn't repeat the word "Connected" twice.
 */
export function marketplaceProvenanceLabel(
  connection: ClientConnection,
): string | null {
  if (!isMarketplaceConnection(connection)) return null;
  const cause = connection.since?.replace(/^Connected\s+/i, "").trim();
  return cause ? `Connected via Sill Marketplace · ${cause}` : "Connected via Sill Marketplace";
}

export function adaptClientDetail(
  detail: ProClientDetail,
  notes: ProClientNote[] = [],
): ClientVM {
  const vm = adaptClientRow(
    {
      sharing_level: detail.sharing_level,
      connection: detail.connection,
      profile: detail.profile,
      verification_status: detail.verification_status,
      last_activity_at: detail.last_activity_at,
      contact_id: detail.contact_id,
      contact_tags: detail.contact_tags ?? [],
      contact_email: detail.contact_email,
      contact_phone: detail.contact_phone,
      last_sent_at: detail.last_sent_at,
      stage: detail.stage,
      stage_updated_at: detail.stage_updated_at,
      dormant: detail.dormant,
    },
    detail.activity_timeline,
  );
  const timeline = mergeTimelineEntries(
    detail.activity_timeline ?? [],
    detail.sent_messages ?? [],
  );
  return {
    ...vm,
    activity: timeline,
    notes: notes.map(adaptClientNote),
    // M4: pass through the contact_note from the detail DTO so the pinned
    // "Contact note" card can render it. Empty string when absent.
    contactNote: detail.contact_note ?? "",
    // Plan 291 round-3: detail-only field, see ClientVM.connectionId.
    connectionId: detail.connection_id,
  };
}

export function adaptClientSentMessages(
  sentMessages: ProSentMessage[],
): ClientActivityEvent[] {
  return [...sentMessages]
    .sort((a, b) => Date.parse(b.sent_at) - Date.parse(a.sent_at))
    .map(adaptSentMessage);
}

// ─────────────────────────────────────────────────────────────────────────
// Today queue.
// ─────────────────────────────────────────────────────────────────────────

export function adaptTodayQueue(
  queue: TodayQueueResponse,
  role: ProRole = "agent",
): TodayQueueVM {
  return {
    groups: (queue.groups ?? []).map((group) => adaptTodayGroup(group, role)),
    snoozed: (queue.snoozed ?? [])
      .map((item) => adaptTodayQueueItem(item, role))
      .filter((item): item is TodaySignalVM => item.kind === "signal"),
    drafts: (queue.drafts ?? []).map(adaptTodayDraft),
    // openCount intentionally excludes drafts (and marketplaceRequests,
    // below) — they are separate action surfaces and must not contribute
    // to the Today badge count.
    marketplaceRequests: queue.pending_marketplace_requests
      ? adaptTodayMarketplaceRequests(queue.pending_marketplace_requests)
      : undefined,
    openCount: queue.open_count ?? 0,
    linkedCount: queue.linked_count ?? 0,
    truncated: queue.truncated ?? false,
  };
}

function adaptTodayDraft(d: TodayDraftResponse): TodayDraftVM {
  return {
    id: d.id,
    automationId: d.automation_id,
    profileId: d.profile_id,
    templateId: d.template_id,
    createdAt: d.created_at,
  };
}

function adaptTodayMarketplaceRequests(
  r: TodayMarketplaceRequestsSummary,
): TodayMarketplaceRequestsVM {
  return {
    count: r.count ?? 0,
    topFeeCents: r.top_fee_cents,
  };
}

function adaptTodayGroup(group: TodayGroup, role: ProRole): TodayGroupVM {
  return {
    key: group.key,
    count: group.count ?? 0,
    items: (group.items ?? []).map((item) => adaptTodayQueueItem(item, role)),
  };
}

function adaptTodayQueueItem(
  item: TodayQueueItem,
  role: ProRole,
): TodayQueueItemVM {
  if (item.group === "your_followups" || item.task) {
    return adaptTodayTaskItem(item);
  }

  const profile = item.profile ?? null;
  const client = adaptClientRow(
    {
      sharing_level: item.sharing_level ?? "status_only",
      connection: {
        link_id: item.item_key,
        profile_id: item.profile_id ?? item.item_key,
        role,
        sharing_level: item.sharing_level ?? "status_only",
        connection_method: "unknown",
        funnel_state: "linked",
        since: undefined,
      },
      profile,
      verification_status: item.verification_status ?? "self_reported",
      last_activity_at: item.last_activity_at,
    },
    [],
  );
  // Unknown stays unknown (PR #638's rule, applied to Today): an absent
  // verification_status means the backend WITHHELD the tier (enrichment
  // locked on a Free plan, or sharing below full financials) — never a
  // self-reported buyer. The embedded ClientVM above still collapses to
  // tier 1 for its internal defaults; the card must read THIS field.
  const verificationTier = item.verification_status
    ? tierRank(item.verification_status)
    : undefined;
  const enrichmentState =
    item.enrichment_state && isEnrichmentState(item.enrichment_state)
      ? item.enrichment_state
      : undefined;
  const reason = todayReason(item.event_type, item.payload);
  const lastSentLabel = item.last_sent_at
    ? `Last sent ${relativeTime(item.last_sent_at)}`
    : undefined;
  const goingCold = isGoingCold(client.minutesAgo);
  return {
    kind: "signal",
    itemKey: item.item_key,
    group: item.group,
    client,
    verificationTier,
    enrichmentState,
    eventType: item.event_type ?? "profile_updated",
    occurredAt: item.occurred_at ?? "",
    reason,
    lastSentAt: item.last_sent_at,
    lastSentLabel,
    lastActivityAt: item.last_activity_at,
    goingCold,
  };
}

function adaptTodayTaskItem(item: TodayQueueItem): TodayTaskItemVM {
  const task = item.task;
  if (!task) {
    return {
      kind: "task",
      itemKey: item.item_key,
      group: item.group,
      task: {
        id: item.item_key,
        title: "Untitled task",
        dueLabel: "No due date",
        createdAt: "",
      },
    };
  }
  const contact = task.contact
    ? {
        id: task.contact.id,
        name: contactDisplayName(task.contact),
        email: task.contact.email ?? undefined,
      }
    : undefined;
  return {
    kind: "task",
    itemKey: item.item_key,
    group: item.group,
    task: {
      id: task.id,
      title: task.title,
      dueAt: task.due_at,
      dueLabel: task.due_at ? relativeDateLabel(task.due_at) : "No due date",
      createdAt: task.created_at,
      doneAt: task.done_at,
      contact,
    },
    contactLabel: contact?.name,
    dueLabel: task.due_at ? relativeDateLabel(task.due_at) : "No due date",
  };
}

function contactDisplayName(
  contact: CRMTask["contact"] extends infer T ? T : never,
): string {
  if (!contact) return "Unnamed contact";
  const full = [contact.first_name, contact.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (full) return full;
  const local = contact.email ? contact.email.split("@")[0] : "";
  return local || "Unnamed contact";
}

function todayReason(
  eventType: TodayQueueItem["event_type"],
  payload: TodayQueueItem["payload"],
): string {
  const p = payload ?? {};
  return (
    p.headline ??
    p.message ??
    p.action ??
    summarizeChangedFields(p.changed_fields) ??
    describeEvent(eventType ?? "profile_updated")
  );
}

function relativeDateLabel(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "No due date";
  const now = Date.now();
  const diffDays = Math.round((then - now) / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return "Due today";
  if (diffDays === 1) return "Due tomorrow";
  if (diffDays === -1) return "Due yesterday";
  if (diffDays > 1) return `Due in ${diffDays} days`;
  return `Due ${Math.abs(diffDays)} days ago`;
}

function isGoingCold(minutesAgo: number): boolean {
  return minutesAgo > 7 * 24 * 60 && minutesAgo < Number.MAX_SAFE_INTEGER;
}

/**
 * Non-identifying label shown when no human name is available. The raw
 * link/profile id is an opaque internal handle and must NEVER be surfaced as a
 * client's name. The backend supplies display_name (synced from Clerk at
 * provisioning, with an email-derived fallback), so this only appears in the
 * brief window before the very first identity sync lands.
 */
const UNNAMED_CLIENT = "Unnamed client";

/**
 * Display name. Connected pro projections carry display_name; target_area stays
 * separate area context and must never be promoted into the identity slot.
 */
function displayName(p: ProfileResponse | null): string {
  if (p?.display_name?.trim()) return p.display_name.trim();
  return UNNAMED_CLIENT;
}

/**
 * Compose enrichment: a client whose consumer identity hasn't synced yet
 * renders as "Unnamed client" — but the agent's own contact book usually has
 * the person's real name already, and a linked contact carries the client's
 * profile id (`linkedProfileId`). Borrow that name (and recompute initials)
 * for any unnamed client with a linked contact. Named clients pass through
 * untouched; the consumer-synced display_name stays authoritative.
 */
export function backfillClientNames(
  clients: ClientVM[],
  contacts: ContactVM[],
): ClientVM[] {
  const nameByProfile = new Map<string, string>();
  for (const c of contacts) {
    if (c.linkedProfileId && c.name && c.name !== "Unnamed contact") {
      nameByProfile.set(c.linkedProfileId, c.name);
    }
  }
  if (nameByProfile.size === 0) return clients;
  return clients.map((cl) => {
    if (cl.name !== UNNAMED_CLIENT) return cl;
    const name = nameByProfile.get(cl.id);
    if (!name) return cl;
    return { ...cl, name, avatar: initials(name) };
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Contacts (book of business).
// ─────────────────────────────────────────────────────────────────────────

/** RFC3339 → "Added Mar 2026" style month label. */
function monthLabel(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

/** Map one contact wire row to the flattened contact view model. */
export function adaptContact(row: ProContactDTO): ContactVM {
  const c = row.contact;
  const full = `${c.first_name} ${c.last_name}`.trim();
  const localPart = c.email ? c.email.split("@")[0] : "";
  const name = full || localPart || "Unnamed contact";
  return {
    id: c.id,
    name,
    avatar: initials(name),
    email: c.email,
    phone: c.phone,
    firstName: c.first_name ?? "",
    lastName: c.last_name ?? "",
    notes: c.notes ?? "",
    tags: c.tags ?? [],
    source: c.source,
    funnelState: row.funnel_state,
    linkedProfileId: c.linked_profile_id,
    added: c.created_at ? `Added ${monthLabel(c.created_at)}` : "",
    area: c.area ?? "",
    horizon: c.horizon ?? "",
    archivedAt: c.archived_at ?? null,
    inviteCooldownUntil: row.invite_cooldown_until ?? null,
    stage: c.stage,
    stageUpdatedAt: c.stage_updated_at,
    // dormant is a sibling of `contact` on the wire (row.dormant, not
    // c.dormant) — it's derived, not part of the CRMContact row model.
    dormant: row.dormant,
  };
}

/** Normalize the zero-filled funnel summary to the view-model shape. */
export function adaptFunnelSummary(
  s: ContactFunnelSummary | undefined,
): ContactFunnelSummaryVM {
  return {
    added: s?.added ?? 0,
    invited: s?.invited ?? 0,
    opened: s?.opened ?? 0,
    signed_up: s?.signed_up ?? 0,
    linked: s?.linked ?? 0,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// CSV import + invite results.
// ─────────────────────────────────────────────────────────────────────────

export function adaptImportPreview(p: ImportPreview): ImportPreviewVM {
  return {
    batchId: p.batch_id,
    filename: p.filename,
    headers: p.headers ?? [],
    suggestedMapping: {
      email: p.suggested_mapping?.email ?? -1,
      first_name: p.suggested_mapping?.first_name ?? -1,
      last_name: p.suggested_mapping?.last_name ?? -1,
      phone: p.suggested_mapping?.phone ?? -1,
    },
    previewRows: p.preview_rows ?? [],
    totalRows: p.total_rows ?? 0,
  };
}

export function adaptImportResult(r: ImportCommitResult): ImportResultVM {
  return {
    imported: r.imported ?? 0,
    skippedDuplicate: r.skipped_duplicate ?? 0,
    invalid: r.invalid ?? 0,
    importedContactIds: r.imported_contact_ids ?? [],
  };
}

export function adaptInviteResult(r: InviteSendResult): InviteResultVM {
  return {
    sent: r.sent ?? 0,
    skippedAlreadySent: r.skipped_already_sent ?? 0,
    skippedCooldown: r.skipped_cooldown ?? 0,
    invalid: r.invalid ?? 0,
    skippedRateLimited: r.skipped_rate_limited ?? 0,
    resetsAt: r.resets_at,
    limit: r.limit,
    plan: r.plan,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Cross-client activity feed (the /activity surface).
// ─────────────────────────────────────────────────────────────────────────

/**
 * Classify an event's urgency tone (the server doesn't send `tone`; we derive
 * it from event_type, mirroring the dashboard signal-feed classification).
 */
function eventTone(
  type: ProActivityEvent["event_type"],
): ActivityFeedItem["tone"] {
  switch (type) {
    case "going_cold":
    case "rate_watch_shift":
    case "lease_window_hit":
      return "hot";
    case "band_up":
    case "invite_accepted":
    case "verification_tier_changed":
    case "document_added":
    case "bank_connected":
      return "good";
    default:
      return "default";
  }
}

/** Map one projected wire event to a cross-client feed item. */
export function adaptFeedItem(e: ProActivityEvent): ActivityFeedItem {
  const base = adaptActivityEvent(e);
  const who = feedDisplayName(e);
  const text = stripLeadingIdentity(base.text, [
    who,
    stringPayload(e.payload.name),
    stringPayload(e.payload.area),
  ]);
  // tier proxy: a magnitude-bearing (full_financial) event is at least doc; a
  // tier_label in the payload wins when present.
  const tier = feedTier(e);
  return {
    id: `${e.profile_id}-${e.occurred_at}-${e.event_type}`,
    type: e.event_type,
    who,
    tier,
    text,
    highlights:
      typeof e.payload.qualitative === "string"
        ? [e.payload.qualitative]
        : undefined,
    magnitude: base.price,
    occurredAt: e.occurred_at,
    time: base.time,
    tone: eventTone(e.event_type),
    profileId: e.profile_id,
  };
}

function feedDisplayName(e: ProActivityEvent): string {
  const displayName = e.display_name?.trim();
  return displayName || UNNAMED_CLIENT;
}

function stringPayload(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

function stripLeadingIdentity(text: string, identities: string[]): string {
  const trimmed = text.trim();
  for (const identity of identities) {
    const value = identity.trim();
    if (!value) continue;
    const prefix = `${value} `;
    if (trimmed.startsWith(prefix)) {
      return sentenceCase(trimmed.slice(prefix.length).trim());
    }
  }
  return trimmed;
}

function sentenceCase(text: string): string {
  if (!text) return text;
  return text[0]!.toUpperCase() + text.slice(1);
}

/**
 * Tier for a feed row from the projected payload — ONLY when the payload
 * actually carries a hint. No hint returns undefined and the row renders no
 * tier chip: the old tier-1 proxy default put a false "Self-reported" badge on
 * every hint-less row, including billing-locked profiles whose tier the
 * backend deliberately strips (SILL-521) — the same false-claim class PR #638
 * removed from the roster.
 */
function feedTier(e: ProActivityEvent): TierRank | undefined {
  const label = (e.payload.tier_label ?? e.payload.tier ?? "")
    .toString()
    .toLowerCase();
  // "secure" matches the backend tier_label "Securely verified"; "bank" is kept
  // for historical events emitted before the label rename.
  if (label.includes("bank") || label.includes("secure")) return 3;
  if (label.includes("doc")) return 2;
  if (label.includes("self")) return 1;
  // A magnitude-bearing event implies full-financial sharing on an unlocked
  // plan (the SILL-521 clamp strips magnitudes for locked profiles).
  return typeof e.payload.list_price === "number" ? 3 : undefined;
}

// ─────────────────────────────────────────────────────────────────────────
// Pro profile + founding (the /profile surface).
// ─────────────────────────────────────────────────────────────────────────

/** Decode a JSONB string[]-ish field defensively. */
function stringList(v: unknown): string[] {
  if (Array.isArray(v))
    return v.filter((x): x is string => typeof x === "string");
  return [];
}

export function adaptProProfile(p: ProProfile): ProProfileVM {
  const isLO = !!p.lo && !p.agent;
  const founding: FoundingVM = {
    member: p.founding?.member ?? false,
    programSlug: p.founding?.program_slug,
    invitesSent: p.founding?.progress?.invites_sent ?? 0,
    clientsLinked: p.founding?.progress?.clients_linked ?? 0,
    clientsVerified: p.founding?.progress?.clients_verified ?? 0,
    goal: p.founding?.target ?? 0,
  };

  if (isLO && p.lo) {
    const lo = p.lo;
    const name = lo.affiliation || "Loan officer";
    return {
      role: "lo",
      name,
      avatar: initials(name),
      brokerage: lo.affiliation ?? "",
      licenseLabel: "NMLS #",
      licenseValue: lo.nmls_number ?? "",
      serviceAreas: stringList(lo.service_areas),
      bio: "",
      directoryPublic: false,
      bookingLink: "",
      founding,
    };
  }

  const agent = p.agent ?? null;
  const name = agent?.brokerage || "Agent";
  return {
    role: "agent",
    name,
    avatar: initials(name),
    brokerage: agent?.brokerage ?? "",
    licenseLabel: "License #",
    licenseValue: agent?.license_num ?? "",
    serviceAreas: stringList(agent?.service_areas),
    bio: agent?.bio ?? "",
    directoryPublic: agent?.directory_public ?? false,
    inviteCode: agent?.invite_code ?? undefined,
    bookingLink: /^https?:\/\//i.test(agent?.booking_link ?? "")
      ? (agent?.booking_link ?? "")
      : "",
    founding,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Notification preferences (the /settings surface).
//
// The view model is a fixed catalog of pro-relevant categories; the wire prefs
// only carry the rows the pro has explicitly set, so we overlay the server
// rows onto the catalog defaults (missing row = the catalog default).
// ─────────────────────────────────────────────────────────────────────────

export type NotificationCatalogSection =
  | "master"
  | "marketplace"
  | "your_book"
  | "existing";

export interface NotificationCatalogEntry extends Omit<
  NotificationPrefVM,
  "frequency"
> {
  /** Missing wire rows hydrate to this presentation default. */
  defaultOn: boolean;
  section: NotificationCatalogSection;
  /** Pinned delivery bypasses preference controls in backend policy. */
  pinned?: boolean;
  pinnedReason?: string;
}

/**
 * Pro notification settings presentation metadata. Backend policy remains the
 * delivery authority; this catalog only owns labels, grouping, and defaults.
 * Entries are keyed by `{channel, category}` — the marketplace section has up
 * to three entries (in-app · email · text) per category.
 */
export const NOTIFICATION_CATALOG: ReadonlyArray<NotificationCatalogEntry> = [
  {
    category: "pro_notifications_email",
    label: "Email notifications",
    description: "Get timely email copies of important Sill notifications.",
    channel: "email",
    onFrequency: "realtime",
    defaultOn: true,
    section: "master",
  },
  // Marketplace: one entry per toggle CELL (in-app · email · text) of the
  // channel matrix — see notification-channel-matrix.ts. Settings renders
  // these grouped one row per category, three switches per row.
  ...marketplaceCatalogEntries(),
  {
    category: "notif_client_activity",
    label: "Client activity",
    description: "Email me about meaningful activity across my client book.",
    channel: "email",
    onFrequency: "realtime",
    defaultOn: true,
    section: "your_book",
  },
  {
    category: "notif_automations",
    label: "Automations",
    description: "Email me when an automation needs my attention.",
    channel: "email",
    onFrequency: "realtime",
    defaultOn: true,
    section: "your_book",
  },
  {
    category: "notif_alerts",
    label: "Send failures",
    description: "Email me when Sill cannot send a message.",
    channel: "email",
    onFrequency: "realtime",
    defaultOn: true,
    section: "your_book",
  },
  {
    category: "agent_outreach",
    label: "Messages from professionals",
    description:
      "Email messages from all agents and loan officers through Sill.",
    channel: "email",
    onFrequency: "realtime",
    defaultOn: true,
    section: "existing",
  },
  {
    category: "agent_client_invite",
    label: "Invitations to Sill",
    description: "Invitations from professionals to join or connect on Sill.",
    channel: "email",
    onFrequency: "realtime",
    defaultOn: true,
    section: "existing",
  },
  {
    category: "marketplace_checkin",
    label: "Marketplace check-ins",
    description: "Follow-up emails asking how your connection is going.",
    channel: "email",
    onFrequency: "realtime",
    defaultOn: true,
    section: "existing",
  },
  {
    category: "marketplace_dormant_nudge",
    label: "Marketplace inactivity reminders",
    description: "Reminders about inactive marketplace connections.",
    channel: "email",
    onFrequency: "realtime",
    defaultOn: true,
    section: "existing",
  },
  {
    category: "marketplace_first_contact_nudge",
    label: "First-contact reminders",
    description: "Reminders to reach out after a marketplace connection.",
    channel: "email",
    onFrequency: "realtime",
    defaultOn: true,
    section: "existing",
  },
  {
    category: "marketplace_review_request",
    label: "Review requests",
    description: "Requests to review a marketplace professional.",
    channel: "email",
    onFrequency: "realtime",
    defaultOn: true,
    section: "existing",
  },
  {
    // CANONICAL slug — must match backend `digestNotificationCategory`
    // (backend/cmd/server/adapters.go) and the unsub token category. The
    // dispatcher only honors an `off` row under THIS category; the pre-SILL-350
    // "weekly_digest" slug was an orphan the dispatcher never read, so the
    // visible opt-out did nothing. Disambiguated from the consumer
    // "weekly_digest" category by the `agent_` prefix.
    category: "agent_weekly_digest",
    label: "Weekly digest",
    description: "A Monday email summarizing buyer activity across your book.",
    channel: "email",
    onFrequency: "weekly",
    defaultOn: true,
    section: "existing",
  },
  {
    category: "new_connection",
    label: "New connections",
    description: "Email me when a buyer connects or accepts an invite.",
    channel: "email",
    onFrequency: "realtime",
    defaultOn: true,
    section: "existing",
  },
  {
    category: "going_cold",
    label: "Going-cold nudges",
    description: "Remind me about connected buyers who have gone quiet.",
    channel: "email",
    onFrequency: "weekly",
    defaultOn: true,
    section: "existing",
  },
] as const;

/** Overlay the server prefs onto the fixed catalog → switch-row view models. */
export function adaptNotificationPrefs(
  rows: NotificationPref[],
): NotificationPrefVM[] {
  const byKey = new Map<string, NotificationPref>();
  for (const r of rows) byKey.set(`${r.channel}:${r.category}`, r);

  return NOTIFICATION_CATALOG.map((entry) => {
    const found = byKey.get(`${entry.channel}:${entry.category}`);
    const frequency = found
      ? found.frequency
      : entry.defaultOn
        ? entry.onFrequency
        : "off";
    return { ...entry, frequency };
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Marketplace payments (M6-A) — the pro Settings Billing section.
// ─────────────────────────────────────────────────────────────────────────

/**
 * Adapts the customer-safe billing status DTO. Must never crash on the
 * zero-value empty struct the handler returns for the no-card case
 * (marketplace_pro_billing.go:70) — a card is only "expired" when one
 * actually exists.
 */
export function adaptBillingStatus(
  wire: BillingStatus,
  now: Date = new Date(),
): BillingStatusVM {
  return {
    hasCard: wire.has_card,
    brand: wire.brand,
    last4: wire.last4,
    expMonth: wire.exp_month,
    expYear: wire.exp_year,
    isExpired: wire.has_card && isCardExpired(wire.exp_month, wire.exp_year, now),
  };
}

/** Straight snake_case → camelCase projection of the pro-scoped rate card. */
export function adaptRateCard(rows: ProRateCardRow[]): RateCardRowVM[] {
  return rows.map((row) => ({
    cause: row.cause,
    tier: row.tier,
    listCents: row.list_cents,
    discountCents: row.discount_cents,
    ...(row.grant_label !== undefined ? { grantLabel: row.grant_label } : {}),
    netCents: row.net_cents,
  }));
}
